/**
 * Helpers for the voice pipeline: one bot line audible at a time, prefetch next while playing.
 */

import { isDisplayableVisitorMessage } from '../utils/visitorMessageDisplay';

function isPlaybackAnchorUserMessage(message) {
  return message?.type === 'user' && isDisplayableVisitorMessage(message.content);
}

/** Next bot bubble whose prior line has finished voice playback. */
export function findNextBotForPlayback(messages, doneSet, startedSet, { getPreviousBotMessage, buildBotMessageSpeechText }) {
  for (let i = 0; i < messages.length; i += 1) {
    const m = messages[i];
    if (m.type !== 'bot') continue;
    if (m.isClosure) continue;
    const prev = getPreviousBotMessage(messages, i);
    if (prev && !doneSet.has(prev.id)) return null;
    if (doneSet.has(m.id)) continue;
    if (startedSet.has(m.id)) return null;
    const text = buildBotMessageSpeechText(m, messages);
    if (!text) return { message: m, speechText: '', empty: true };
    return { message: m, speechText: text, empty: false };
  }
  return null;
}

/** Last bot bubble after the most recent visitor line in transcript order. */
export function findLastBotAfterLastUserMessage(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  let lastUserIdx = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (isPlaybackAnchorUserMessage(m)) {
      lastUserIdx = i;
      break;
    }
  }
  if (lastUserIdx < 0) return null;
  let lastBot = null;
  for (let i = lastUserIdx + 1; i < messages.length; i += 1) {
    if (messages[i].type === 'bot') lastBot = messages[i];
  }
  return lastBot;
}

/** Chat view: only the final bot line after the latest user message; skip earlier lines in that run. */
export function findNextBotForChatPlayback(
  messages,
  doneSet,
  startedSet,
  { buildBotMessageSpeechText },
) {
  const target = findLastBotAfterLastUserMessage(messages);
  if (!target) return { next: null, skipIds: [] };

  let lastUserIdx = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (isPlaybackAnchorUserMessage(m)) {
      lastUserIdx = i;
      break;
    }
  }
  const skipIds = [];
  for (let i = lastUserIdx + 1; i < messages.length; i += 1) {
    const m = messages[i];
    if (m.type === 'bot' && m.id !== target.id) skipIds.push(m.id);
  }

  if (doneSet.has(target.id) || startedSet.has(target.id)) {
    return { next: null, skipIds };
  }
  const text = buildBotMessageSpeechText(target, messages);
  if (!text) return { next: { message: target, speechText: '', empty: true }, skipIds };
  return { next: { message: target, speechText: text, empty: false }, skipIds };
}

/** Bot message immediately after `afterBotId` in transcript order (skips user rows). */
export function findFollowingBotMessage(messages, afterBotId) {
  let seenTarget = false;
  for (let i = 0; i < messages.length; i += 1) {
    const m = messages[i];
    if (m.type !== 'bot') continue;
    if (m.id === afterBotId) {
      seenTarget = true;
      continue;
    }
    if (seenTarget) return m;
  }
  return null;
}

/** During awakening intro: only the last bot line currently visible in chat. */
export function findLastDisplayedBotForPlayback(
  messages,
  doneSet,
  startedSet,
  revealedSet,
  { buildBotMessageSpeechText },
) {
  const displayed = messages.filter(
    (m) => m.type === 'bot' && revealedSet.has(m.id),
  );
  if (displayed.length === 0) return null;
  const last = displayed[displayed.length - 1];
  if (doneSet.has(last.id) || startedSet.has(last.id)) return null;
  const text = buildBotMessageSpeechText(last, messages);
  if (!text) return { message: last, speechText: '', empty: true };
  return { message: last, speechText: text, empty: false };
}
